<script setup lang="ts">
import { mdiBellAlertOutline, mdiCheckCircleOutline, mdiPencil } from "@mdi/js";
import { AlertSummary, ConditionType } from "@soft-db/monitoring-api-client";
import { Button, Icon, makeColumnHelper } from "@soft-db/ui";
import { PropType } from "vue";
import AddButtonMenu from "../common/AddButtonMenu.vue";
import AppDataTable from "../common/AppDataTable.vue";
import AlertDeleteButton from "./AlertDeleteButton.vue";

defineProps({
	alerts: {
		type: Array as PropType<AlertSummary[]>,
		required: true,
	},
});
const emit = defineEmits(["click:create", "click:edit", "click:import"]);

const { t } = useI18n();

function typeText(type: ConditionType) {
	switch (type) {
		case "record":
			return t("alert-settings.record");
		case "vibration_event":
			return t("dashboard.vibrationEvent");
		case "weekly_limit":
			return t("common.weeklyLimit");
	}
}

function createAlert() {
	emit("click:create");
}
function editAlert(id: string) {
	emit("click:edit", id);
}
function importAlert() {
	emit("click:import");
}

const { Column } = makeColumnHelper<AlertSummary>();
</script>

<template>
	<AppDataTable layout="simple" :data="alerts" :pagination="{ pageIndex: 0, pageSize: 10 }">
		<template #title>
			<h2 class="flex items-center gap-2">
				{{ t("object.alert", 2) }}
				<Icon :d="mdiBellAlertOutline" />
			</h2>
		</template>
		<template #action>
			<AddButtonMenu
				:addText="t('actions.createAlert')"
				:importText="t('alert-settings.importAlert')"
				@click:add="createAlert"
				@click:import="importAlert"
			/>
		</template>
		<template #columns>
			<Column field="name" :header="t('common.name')" enableSorting />
			<Column
				v-slot="{ row: { original: alert } }"
				field="type"
				:header="t('common.type')"
				enableSorting
			>
				<span>{{ typeText(alert.type) }}</span>
			</Column>
			<Column
				v-slot="{ row: { original: alert } }"
				field="enabled"
				:header="t('common.enabled')"
				shrink
			>
				<Icon v-if="alert.enabled" class="text-success" :d="mdiCheckCircleOutline" />
			</Column>
			<Column v-slot="{ row: { original: alert } }" shrink>
				<div class="flex gap-1">
					<Button color="primary" variant="soft" shape="square" @click="editAlert(alert.id)">
						<Icon :d="mdiPencil" />
					</Button>
					<AlertDeleteButton :id="alert.id" />
				</div>
			</Column>
		</template>
	</AppDataTable>
</template>
